import { BankOutlined, EditOutlined } from '@ant-design/icons';
import {
	AutoComplete,
	Avatar,
	Button,
	DatePicker,
	Input,
	InputNumber,
	List,
	Menu,
	Progress,
	Row,
	Select,
	Steps,
	Tabs,
	Tag,
	TimePicker
} from 'antd';
import propTypes from 'prop-types';
import React, { forwardRef, HTMLAttributes } from 'react';
import { InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { nullCoalesc } from '../../utils/objects';

const DefaultTitleSize = 'small';
const TitleSizeMap = {
	small: '14px',
	medium: '18px',
	large: '22px'
};

const applicantStateMap = {
	large: {
		count: {
			fontSize: '20px'
		},
		name: {
			fontSize: '0.5rem'
		}
	},
	mobile: {
		width: '17%',
		minWidth: '0',
		count: {
			fontSize: '18px'
		},
		name: {
			fontSize: '0.5rem'
		}
	}
};

const Defaults = {
	inputFieldBorderRadius: '.3rem'
};
export const LogoBox = styled.img`
	box-shadow: 0 2px 2px 1px rgba(0, 0, 0, 0.15), 0 1px 1px rgba(0, 0, 0, 0.2);
	border-radius: 14px;
	width: 75%;
`;

export const BoxTitle = styled.p<{ size?: any }>`
	font-size: ${({ size }) => TitleSizeMap[size || DefaultTitleSize]};
	padding: 0.8rem;
	font-weight: 700;
	line-height: 1.8;
	margin: 0;
	border-bottom: 1px solid #e8e8e8;
	color: ${({ theme }) => theme.TA_BLUISH_GREY};
`;

//NOTE used these backface and translateZ due to this issue
//https://css-tricks.com/forums/topic/transforms-cause-font-smoothing-weirdness-in-webkit/
//https://stackoverflow.com/questions/40012420/chrome-animation-makes-text-blurry

export const JobSummaryItem = styled(Row)`
	transition: 0.5s all;
	padding: 1.5rem 1rem;
	padding-left: 2.3rem;
	position: relative;
	overflow: hidden;
	border: 1px solid #e8e8e8;
	min-height: 80px;

	&::before {
		content: '';
		position: absolute;
		width: 1%;
		background-color: ${({ theme }) => theme.TA_GREEN};
		top: 0;
		bottom: 0;
		left: -1%;
		transition: all 0.4s;
	}

	&:hover {
		background-color: rgba(240, 240, 242, 0.3);
		&::before {
			display: block;
			left: 0;
		}
	}
`;

export const JobListItem = styled(List.Item)`
	border: none;
	padding: 0;
`;

export const TitleLink = styled(Link)<any>`
	color: ${({ color }) => color || '#010101'};
	font-weight: 500;
	text-align: ${({ textAlign }) => textAlign || 'left'};
	line-height: ${({ lineHeight }) => {
		if (lineHeight) return `${lineHeight}px`;
		return 1.8;
	}};
	font-size: ${({ size }) => size || 18}px;
	letter-spacing: 0.5px;
	display: flex;
	align-items: center;
	margin-top: ${({ marginTop }) => nullCoalesc(marginTop, 0)}px;
	margin-bottom: ${({ marginBottom }) => nullCoalesc(marginBottom, 0)}px;
`;

const _Title = ({ icon, iconType, text, children, theme, className, ...rest }) => {
	const content = (
		<div className={className} {...rest}>
			{icon}
			{children || <span>{text}</span>}
		</div>
	);
	return content;
};

export const Title = styled(_Title)`
	cursor: ${({ button }) => (button ? 'pointer' : 'default')};
	display: flex;
	align-items: center;
	color: ${({ color }) => color || 'inhirit'};
	font-size: 16px;
`;

const overlayStyles = ({ size, lineHeight }) => css`
	text-align: center;
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	opacity: 0;
	font-size: ${size || 20}px;
	font-weight: 500;
	color: #ffffff;
	line-height: ${lineHeight || 200}px;
	transition: 0.3s ease-out;
`;

const imageWindowAfterPs = () => css`
	content: '';
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	background-color: transparent;
	transition: 0.3s ease-out;
	z-index: 100;
	border-radius: 0;
`;
export const _CoverPicture = ({ src, size, className, onClick }: any) => (
	<div className={className} onClick={onClick}>
		<img className="photo" src={src} />
	</div>
);

export const CoverPicture = styled(_CoverPicture)<{ editable?: boolean }>`
	position: relative;
	width: 100%;
	height: ${({ size }) => (size === 'small' ? 180 : 330)}px;
	background-color: #333541;
	cursor: ${({ onClick }) => (onClick ? 'pointer' : 'initial')};
	.photo {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
	${({ editable }) =>
		editable &&
		css`
			& > .overlay-inner-text {
				${overlayStyles({ size: 20, lineHeight: 120 })}
				font-size: 4rem;
				font-weight: 700;
			}
			&:hover {
				opacity: 1;
				& > .overlay-inner-text {
					opacity: 1;
				}
				&::after {
					background-color: rgba(0, 0, 0, 0.26);
				}
			}
			&::after {
				${imageWindowAfterPs()}
				border-radius: none;
			}
		`}
`;

const _Avatar = ({ className, src, onClick, editable, showDefault = false, ...rest }) => (
	<div className={className} onClick={onClick}>
		{!showDefault ? (
			<img className="photo" src={src} {...rest} />
		) : (
			<Avatar shape="square" icon={<BankOutlined />} className="photo photo-icon" />
		)}
		{editable && (
			<span className="overlay-inner-text">
				<EditOutlined />
			</span>
		)}
	</div>
);

export const AvatarWindow = styled(_Avatar)`
	cursor: ${({ onClick }) => (onClick ? 'pointer' : 'initial')};
	width: ${({ size }) => {
		if (size) return `${size}px`;
		return '100%';
	}};
	height: ${({ size }) => {
		if (size) return `${size}px`;
		return '100%';
	}};
	position: relative;
	overflow: hidden;
	border: 5px solid #e8e8e8;
	border-radius: 12px;
	background-color: #333541;
	.photo {
		width: 100%;
		height: 100%;
		/* border-radius: 10px; */
		object-fit: cover;
		&.photo-icon {
			font-size: 60px;
			line-height: ${({ lineHeight }) => lineHeight || 120}px;
		}
	}

	${({ editable }) =>
		editable &&
		css`
			& > .overlay-inner-text {
				${overlayStyles({ size: 20, lineHeight: 120 })}
			}
			&:hover {
				& > .overlay-inner-text {
					opacity: 1;
				}
				&::after {
					background-color: rgba(0, 0, 0, 0.26);
				}
			}
			&::after {
				${imageWindowAfterPs()}
			}
		`}
`;

export const TASteps = styled(Steps)`
	& .ant-steps-item-process .ant-steps-item-icon {
		background-color: #ffffff;
		border-color: ${({ theme }) => theme.TA_GREEN};
	}
	& .ant-steps-item-process .ant-steps-item-icon > .ant-steps-icon {
		color: ${({ theme }) => theme.TA_GREEN};
	}
	& .ant-steps-item-finish .ant-steps-item-icon,
	& .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-content > .ant-steps-item-title::after {
		background-color: ${({ theme }) => theme.TA_GREEN};

		border-color: ${({ theme }) => theme.TA_GREEN};
	}
	& .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
		color: #ffffff;
	}
`;

export const TAMenu = styled(Menu)`
	border: none;
`;
export const TAMenuItem = styled(Menu.Item)`
	font-family: ubuntu;
	font-size: 15px !important;
	font-weight: 500 !important;
	color: #23292f;
	&::after {
		display: none;
	}
	&.ant-menu-item-selected,
	&:hover,
	&-active,
	.ant-menu:not(.ant-menu-inline) .ant-menu-submenu-open,
	.ant-menu-submenu-active,
	.ant-menu-submenu-title:hover {
		background-color: #e6f7ff;
		color: ${({ theme }) => theme.TA_GREEN};
	}
`;

const _TASelect = forwardRef<any, any>(
	(
		{
			options,
			className,
			children,
			optionValueIndex = 'name',
			optionKeyIndex = 'id',
			optionNameIndex = 'name',
			allowClear = true,
			...rest
		},
		ref
	) => {
		return (
			<Select
				showSearch
				allowClear={allowClear}
				optionFilterProp={options && 'children'}
				filterOption={
					options &&
					((input, option) => option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0)
				}
				className={`ta-ant-input ${className}`}
				{...rest}
				autoComplete="false"
				ref={ref}
			>
				{options
					? options.map(opt => (
							<Select.Option value={opt[optionValueIndex]} key={opt[optionKeyIndex]}>
								{opt[optionValueIndex]}
							</Select.Option>
					  ))
					: children}
			</Select>
		);
	}
);

export const TASelect = styled(_TASelect)`
	width: 100%;
	height: 44px;

	.ta-form &.ant-select-single .ant-select-selector,
	.ta-form &.ant-select-multiple .ant-select-selector {
		background-color: ${({ theme }) => theme.TA_LIGHT} !important;
	}
	.ta-form &.ant-select-multiple .ant-select-selector {
		min-height: 44px;
		height: auto;
	}
	&.ant-select-single .ant-select-selector,
	&.ant-select-multiple .ant-select-selector {
		/* background-color: ${({ theme }) => theme.TA_LIGHT} !important; */
		height: 100%;
		border-radius: ${Defaults.inputFieldBorderRadius};

		/* .ant-select-selection__rendered {
			height: 100%;
			position: relative;
			.ant-select-selection-selected-value {
				height: 100%;
				position: absolute;
				top: 19%;
				left: 0; //one way of centering

				// padding-top: 2%; //another way but needs handling for small screens
				// display: flex !important; //another way but with a bug
				// align-items: center;
			}

			ul li {
				&.ant-select-selection__choice,
				&.ant-select-search.ant-select-search--inline {
					margin-top: 10px;
				}
			}
		} */

		.ant-select-selection-overflow {
			.ant-select-selection-overflow-item .ant-select-selection-item {
				background-color: #fafafa;
			}
		}
		.ant-select-selection-item,
		.ant-select-selection-placeholder {
			display: flex;
			align-items: center;
		}
		.ant-select-selection-search {
			height: 100%;
			input {
				height: 100%;
			}
		}
	}
	.ant-select-clear {
		background-color: ${({ theme }) => theme.TA_LIGHT} !important;
	}

	&:not(.ant-select-disabled):hover .ant-select-selector,
	&:not(.ant-select-customize-input).ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
		border-color: ${({ theme }) => theme.TA_GREEN};
	}

	.ant-select-selection.ant-select-selection--multiple input {
		border: none;
		&:hover,
		&:focus {
			border: none;
		}
	}
`;

const _TAAutoComplete = ({
	options,
	className,
	children,
	optionValueIndex = 'name',
	optionKeyIndex = 'id',
	optionNameIndex = 'name',
	allowClear = true,
	...rest
}) => (
	<AutoComplete
		allowClear={allowClear}
		optionFilterProp={options && 'children'}
		filterOption={
			options && ((input, option) => option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0)
		}
		className={`ta-ant-input ${className}`}
		{...rest}
	>
		{options
			? options.map(opt => (
					<Select.Option value={opt[optionValueIndex]} key={opt[optionKeyIndex]}>
						{opt[optionValueIndex]}
					</Select.Option>
			  ))
			: children}
	</AutoComplete>
);
export const TAAutoComplete = styled(_TAAutoComplete)`
	width: 100%;
	height: 44px;

	&.ant-select .ant-select-selector {
		background-color: ${({ theme }) => theme.TA_LIGHT} !important;
		height: 100%;
		border: 1px solid #d9d9d9;
		transition: 0.3s;

		.ant-select-selection-search {
			height: 100%;
			input {
				height: 100%;
				padding-left: 12px;
				border-radius: 4px;
				border: none;

				&:hover,
				&:focus {
					border: none;
				}
			}

			/* ul,
					ul li {
						height: 100%;
						display: flex !important;
						align-items: center;

						input {
							height: 100%;
						}
					} */
		}
	}
`;
const _ApplicantsStateCount = ({ count, name, className }: any) => (
	<span className={className}>
		<span className="count">{count || 0}</span>
		<span className="name">{name}</span>
	</span>
);

export const ApplicantsStateCount = styled(_ApplicantsStateCount)<{ size?: any; color?: string }>`
	display: inline-block;
	width: ${({ size }) => applicantStateMap[size].width || 'auto'};
	min-width: ${({ size }) => applicantStateMap[size].minWidth || '50px'};
	padding: 0;
	text-align: center;
	/* margin-right: 0.8rem; */

	.count {
		text-align: center;
		display: block;
		width: 100%;
		font-size: ${({ size }) => applicantStateMap[size].count.fontSize || '15px'};
		color: ${({ color }) => color || '#999'};
		font-weight: 700;
		/* margin-right: 0.8rem; */
	}
	.name {
		font-size: ${({ size }) => applicantStateMap[size].name.fontSize || '0.3rem'};
		color: ${({ color }) => color || '#999'};
		white-space: nowrap;
	}
	&:not(:last-child) {
		border-right: 1px solid #bdbdc5;
	}
	@media (min-width: 1200px) {
		padding: 0.2rem 0.8rem;
		.name {
			font-size: ${({ size }) => (size === 'large' ? 13 : 12)}px;
		}
	}
`;

export const KeywordTag = styled.span`
	border-radius: 13px;
	background-color: #ffffff;
	color: #3f536e;
	font-size: 0.9rem;
	line-height: 25px;
	padding: 2px 10px;
	border: 1px solid #e0e1e2;
	transition: all 0.3s;
	margin-right: 5px;
	text-align: center;
	cursor: default;
	white-space: nowrap;
	display: inline-block;

	&:hover {
		background-color: ${({ theme }) => theme.TA_GREEN};
		color: #ffffff;
	}
`;

export const SectionTitle = styled.span`
	color: #2c405a;
	font-size: 36px;
	line-height: 44px;
	font-weight: 500;
`;

export const FormSectionTitle = styled.p`
	color: #23292f;
	font-size: 24px;
	line-height: 50px;
	font-weight: 500;
	margin-bottom: 0;
`;

export const HighlightedDashboardSubtitle: React.FC<HTMLAttributes<HTMLSpanElement>> = ({ children, className }) => (
	<span
		style={{
			color: '${({theme})=>theme.TA_GREEN}',
			borderBottom: '2px dashed ${({theme})=>theme.TA_GREEN}',
			fontSize: '16px',
			marginLeft: '5px'
		}}
		className={className}
	>
		{children}
	</span>
);

// const slidDown = keyframes`
// 	from {
// 		display: none;
// 		height: 0;
// 		line-height: 0;
// 		padding: 0;
// 	} to {
// 		height: 300px;
// 		display: block;
// 		line-height: auto;
// 		padding: 12px;
// 	}
// `;
// export const GhostBox = styled.div`
// 	height: 0;
// 	padding: 0;
// 	line-height: 0;

// 	animation: ${({ visible }) =>
// 		visible
// 			? css`
// 					${slidDown} .5s linear ease-out
// 			  `
// 			: 'none'};
// `;
export const SkillFormContainer = styled.div`
	background-color: #1edcab0a;
	padding: 12px;
	position: relative;

	& .slide-close-btn,
	& .slide-close-btn:hover,
	& .slide-close-btn:focus,
	& .slide-close-btn:active {
		position: absolute;
		top: 0;
		right: 0;
		background-color: transparent;
		border: none;
		box-shadow: none;
		outline: 0;
		padding: 16px 12px;
		color: #a8c6df;
		font-size: 16px;
	}
`;
export const ActionList = styled.div<{ align?: string }>`
	& button + button {
		margin-left: 8px;
	}

	display: flex;
	justify-content: ${({ align }) => (align === 'left' ? 'flex-start' : 'flex-end')};
`;

export const SidebarCompanyName = styled.span`
	color: #ffffff;
	font-size: 0;
	/* opacity: 0; */
	transition: font-size 0.4s, transform 0.2s;
	margin-top: 0.5rem;
	word-break: break-all;
	transform: scale(0);

	&.visible {
		font-size: 1.3rem;
		transform: scale(1);
	}
`;

export const TAProgress = styled(Progress)`
	& .ant-progress-inner {
		background-color: ${({ trailColor }) => trailColor || '#dedede'};
	}
	& div {
		display: flex;
		align-items: center;

		.ant-progress-text {
			width: auto;
		}
	}
`;

export const SBAvatar = styled(Avatar)`
	width: 60px;
	height: 60px;
	line-height: 60px;
	font-size: 30px;
	transition: 0.3s ease-in-out;

	& .lg {
		width: 120px;
		height: 120px;
		line-height: 120px;
		font-size: 60px;
	}
`;

export const TATabs = styled(Tabs)`
	& .ant-tabs-nav-list .ant-tabs-tab {
		&:active,
		&.ant-tabs-tab-active,
		&.ant-tabs-tab-active .ant-tabs-tab-btn,
		.ant-tabs-tab-remove:focus,
		.ant-tabs-tab-btn:active,
		.ant-tabs-tab-remove:active {
			/* color: #20af8a; */
			font-weight: normal;
		}
		&:hover {
			color: ${({ theme }) => theme.TA_GREEN};
		}
	}

	& .ant-tabs-nav::before {
		border: 0;
	}

	& .ant-tabs-ink-bar.ant-tabs-ink-bar-animated {
		/* background-color: #20af8a; */
	}
	& .ant-tabs-tab-disabled {
		&:hover {
			color: rgba(0, 0, 0, 0.25) !important;
		}
	}
	.ant-tabs-tab-btn:focus {
		color: rgba(0, 0, 0, 0.65);
	}
`;

TATabs.TabPane = Tabs.TabPane;

export const TADatePicker = styled(DatePicker)`
	.ta-form & {
		background-color: #fafafa;
	}
	border-radius: ${Defaults.inputFieldBorderRadius};

	&:hover input,
	input {
		border: none !important;
		background-color: transparent;
	}

	&:hover,
	&:focus,
	&.ant-picker-focused {
		border: 1px solid ${({ theme }) => theme.TA_GREEN};
	}
`;

export const TATimePicker = styled(TimePicker)`
	.ta-form & {
		background-color: #fafafa;
	}
	border-radius: ${Defaults.inputFieldBorderRadius};

	&:hover input,
	input {
		border: none !important;
		background-color: transparent;
	}

	&:hover,
	&:focus,
	&.ant-picker-focused {
		border: 1px solid ${({ theme }) => theme.TA_GREEN};
	}
`;

export const TAInputGroup = styled(InputGroup)`
	& > *:last-child {
		border-radius: 0 ${Defaults.inputFieldBorderRadius} ${Defaults.inputFieldBorderRadius} 0 !important;
	}

	.ant-select .ant-select-selector {
		border-radius: ${Defaults.inputFieldBorderRadius} 0 0 ${Defaults.inputFieldBorderRadius} !important;
	}
`;
